/* eslint-disable react-hooks/rules-of-hooks */
import ReactMarkdown from 'react-markdown';
import { CodeBlock, katekToLatex, Skeleton } from '@scale/scaleui';
import { twMerge } from 'tailwind-merge';
import { extractCodeBlocks } from 'react-scale/components/scalers/tasks/ThreadedInteraction/coding/helpers';
import ExecutableCodeBlock from 'react-scale/components/scalers/tasks/ThreadedInteraction/coding/ExecutableCodeBlock';
import _ from 'lodash';
import React, { RefObject, useContext, useMemo, useRef } from 'react';
import { PluggableList } from 'react-markdown/lib/react-markdown';
import rehypeKatex from 'rehype-katex';
import rehypeRaw from 'rehype-raw';
import rehypeSanitize from 'rehype-sanitize';
import remarkBreaks from 'remark-breaks';
import remarkGfm from 'remark-gfm';
import remarkMath from 'remark-math';
import { useUnleashFeatureFlagWithLoading } from 'react-scale/hooks/useUnleashFeatureFlag';
import { ChatTaskContext } from 'react-scale/components/scalers/tasks/ThreadedInteraction/contexts/ChatTaskContext';
import {
  EditorContainer,
  Preview,
  MarkdownPreviewContext,
  PreviewFloatingMenu,
  ReviewerCopilot,
} from 'react-scale/components/scalers/tasks/ThreadedInteraction/components/editor';
import { useMarkdownSyntaxOptions } from 'react-scale/components/scalers/tasks/ThreadedInteraction/hooks/useMarkdownSyntaxOptions';
import WorkerCommentBridge from '../definitions/ModelResponseEditor/ModelResponseEditorTaskComponent/WorkerCommentBridge';
import { TextSelectionPopover } from '../definitions/ModelResponseEditor/ModelResponseEditorTaskComponent/TextSelectionPopover';
import dynamic from 'next/dynamic';

const ChatAudioEditor = dynamic(
  () => import('../attachments/audio-attachments/ChatAudioEditor').then(mod => mod.ChatAudioEditor),
  { ssr: false },
);

export const ICON_MARKDOWN_PROPS = {
  className: '',
};

export type ChatMarkdownProps = {
  markdown: string;
  className?: string;
  autoRunDependenciesComplete?: boolean;
  enableMath?: boolean;
  enableSingleDollarTextMath?: boolean;
  enableHtml?: boolean;
  enableSoftBreaks?: boolean;
  enableExecutableCodeBlocks?: boolean;
  disableRehypeSanitize?: boolean;
  loading?: boolean;
  defaultExpandCodeBlocks?: boolean;
  enableRTL?: boolean;

  // for inline comments
  enableInlineComments?: boolean;
  childId?: string;
  turnIndex?: number;
  // note: this probably can be done in a better way
  cardRef?: RefObject<HTMLDivElement>;
  // note: I'm sure this can also be done in a better way. Inherited from MilkdownViewer
  overrideChildId?: string;
  // note: this is used to prevent debounce for streamed response content updates
  shouldDebounceContentUpdates?: boolean;
};

const styles = `
  li pre {
    padding: 0 !important;
    border-radius: 0.5rem !important;
  }
`;

/**
 * This component was created using this
 * https://www.figma.com/file/l95vakZr0H0QjwMoAtaS3i/Chat-Task-%E2%80%94-Quick-Improvements?type=design&node-id=615-3783&mode=design&t=SfFCXdAte0bakHmD-0
 * mockup as reference
 */

const ChatMarkdownUnmemoized = (props: ChatMarkdownProps) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const taskContext = useContext(ChatTaskContext);
  const markdownSyntaxConfig = useMarkdownSyntaxOptions();
  const { canEditComments } = taskContext;
  const isTextSelectionCommentEnabled = props.enableInlineComments && !!canEditComments;
  const childId = props.childId;

  const { data: shouldMigrateToPreviewComponent, isLoading } = useUnleashFeatureFlagWithLoading(
    'chat-preview-migration',
    { projectId: taskContext.projectId },
    false,
    true,
    {
      refetchOnMount: false,
      refetchOnReconnect: false,
      refetchOnWindowFocus: false,
      refetchInterval: false,
      refetchIntervalInBackground: false,
      retry: false,
    },
  );

  if (isLoading) {
    return <Skeleton className="w-full h-12 rounded" />;
  }

  /**
   * TODO(@mrkev)
   * The following properties are handled/are missing
   * by MarkdownPreviewContext vs old renderer:
   * DONE markdown: string;
   * DONE className?: string;
   *      autoRunDependenciesComplete?: boolean;
   * DONE enableMath?: boolean;
   *      enableSingleDollarTextMath?: boolean;
   *      enableHtml?: boolean;
   *      enableSoftBreaks?: boolean;
   * DONE enableExecutableCodeBlocks?: boolean;
   *      disableRehypeSanitize?: boolean;
   *      loading?: boolean;
   * DONE defaultExpandCodeBlocks?: boolean;
   * DONE enableRTL?: boolean;
   */
  if (shouldMigrateToPreviewComponent) {
    return (
      <MarkdownPreviewContext
        className="min-h-fit"
        content={props.markdown}
        enableMath={props.enableMath}
        enableHtml={props.enableHtml}
        enableSoftBreaks={props.enableSoftBreaks}
        defaultTokens={markdownSyntaxConfig}
        enableSingleDollarTextMath={props.enableSingleDollarTextMath}
        enableCodeExecution={props.enableExecutableCodeBlocks}
        defaultExpandCodeBlocks={props.defaultExpandCodeBlocks}
        shouldDebounceContentUpdates={props.shouldDebounceContentUpdates}
      >
        <EditorContainer ref={containerRef} className="border-none">
          <PreviewFloatingMenu />
          <Preview
            className={props.className}
            textDirection={props.enableRTL ? 'rtl' : undefined}
          />
        </EditorContainer>
        <ReviewerCopilot anchorRef={containerRef} enabled={taskContext.enableFactChecker} />

        {/* Comments */}
        {isTextSelectionCommentEnabled && childId != null && (
          <WorkerCommentBridge enabled={isTextSelectionCommentEnabled} childId={childId} />
        )}
        {isTextSelectionCommentEnabled && childId != null && (
          <TextSelectionPopover
            anchorRef={props.cardRef ?? containerRef}
            enabled={isTextSelectionCommentEnabled}
            childId={childId}
            turnIndex={props.turnIndex}
          />
        )}
      </MarkdownPreviewContext>
    );
  }
  return <LegacyChatMarkdownUnmemoized {...props} />;
};

const TableCell = ({
  as: Component,
  ...props
}: {
  as: keyof JSX.IntrinsicElements;
  children?: React.ReactNode;
}) => (
  <Component {...props}>
    {React.Children.map(props.children, child => (child === '<br>' ? <br /> : child))}
  </Component>
);

const LegacyChatMarkdownUnmemoized = ({
  markdown,
  className,
  autoRunDependenciesComplete,
  enableMath,
  enableSingleDollarTextMath,
  enableHtml,
  enableSoftBreaks,
  enableExecutableCodeBlocks,
  disableRehypeSanitize = true,
  loading = false,
  defaultExpandCodeBlocks = true,
  enableRTL,
}: ChatMarkdownProps) => {
  const rehypePlugins = useMemo(
    () =>
      _.compact([
        enableHtml ? rehypeRaw : null,
        enableHtml && !disableRehypeSanitize ? rehypeSanitize : null,
        enableMath ? rehypeKatex : null,
      ]),
    [enableHtml, enableMath, disableRehypeSanitize],
  );

  const remarkPlugins = useMemo(() => {
    const plugins: PluggableList = [remarkGfm];
    if (enableMath) {
      plugins.push([remarkMath, { singleDollarTextMath: !!enableSingleDollarTextMath }]);
    }
    if (enableSoftBreaks) {
      plugins.push(remarkBreaks);
    }
    return plugins;
  }, [enableMath, enableSingleDollarTextMath, enableSoftBreaks]);

  if (enableMath) {
    markdown = replaceWithDoubleDollarSign(markdown);
    markdown = katekToLatex(markdown);
  }

  const codeBlocks = extractCodeBlocks(markdown);
  return (
    <div
      dir={enableRTL ?? false ? 'rtl' : undefined}
      className={enableRTL ? 'text-right' : undefined}
    >
      <style>{styles}</style>
      <ReactMarkdown
        remarkPlugins={remarkPlugins}
        rehypePlugins={rehypePlugins}
        // eslint-disable-next-line react/no-children-prop
        children={markdown}
        className={twMerge(
          'prose prose-neutral prose-sm max-w-none break-words [&>pre]:p-0 [&>pre]:rounded-lg',
          className,
          enableMath ? 'text-neutral-700' : '', // HACK: This is a hack to fix the color of LaTeX blocks
        )}
        components={{
          table(props) {
            return (
              <div className="overflow-x-auto">
                <table {...props} />
              </div>
            );
          },
          th(props) {
            return <TableCell as="th" className="text-neutral-700" {...props} />;
          },
          td(props) {
            return <TableCell as="td" className="text-neutral-600" {...props} />;
          },
          p(props) {
            return <p className="text-neutral-700" {...props} />;
          },
          h1(props) {
            return <h1 className="text-neutral-700" {...props} />;
          },
          h2(props) {
            return <h2 className="text-neutral-700" {...props} />;
          },
          h3(props) {
            return <h3 className="text-neutral-700" {...props} />;
          },
          h4(props) {
            return <h4 className="text-neutral-700" {...props} />;
          },
          h5(props) {
            return <h5 className="text-neutral-700" {...props} />;
          },
          h6(props) {
            return <h6 className="text-neutral-700" {...props} />;
          },
          ul(props) {
            return <ul className="text-neutral-700" {...props} />;
          },
          ol(props) {
            return <ol className="text-neutral-700" {...props} />;
          },
          li(props) {
            return <li className="text-neutral-700" {...props} />;
          },
          strong(props) {
            return <strong className="text-neutral-700" {...props} />;
          },
          a(props) {
            return (
              <a
                target="_blank"
                rel="noopener noreferrer"
                {...props}
                className={twMerge('text-neutral-700', props.className)}
              />
            );
          },
          code(props) {
            const { children, className = '', node, inline, ...rest } = props;
            const LANGUAGE_MATCH_REGEX = /language-(\w+)/;
            const languageMatch = LANGUAGE_MATCH_REGEX.exec(className);
            const codeBlockIndex = codeBlocks.findIndex(
              codeBlock => codeBlock.code === String(children).replace(/\n$/, ''),
            );

            return !inline && enableExecutableCodeBlocks ? (
              <ExecutableCodeBlock
                code={String(children).replace(/\n$/, '')}
                languageMatch={languageMatch}
                codeBlocksInMarkdownText={codeBlocks}
                codeBlockIndex={codeBlockIndex}
                autoRunDependenciesComplete={autoRunDependenciesComplete}
                loading={loading}
                defaultExpanded={defaultExpandCodeBlocks}
              />
            ) : !inline ? (
              <CodeBlock
                code={String(children).replace(/\n$/, '')}
                languageMatch={languageMatch}
                initCollapsed={!defaultExpandCodeBlocks}
              />
            ) : (
              <code {...rest} className={twMerge('text-neutral-700', className)}>
                {children}
              </code>
            );
          },
          pre(props) {
            return enableExecutableCodeBlocks ? <div>{props.children}</div> : <pre {...props} />;
          },
          audio(props) {
            const sourceElement = props.children?.find(
              (child): child is React.ReactElement =>
                React.isValidElement(child) &&
                typeof child.type === 'string' &&
                child.type === 'source',
            );
            const audioSrc = sourceElement?.props?.src || props.src;

            if (!audioSrc || typeof audioSrc !== 'string') {
              return <audio {...props} />;
            }

            return <ChatAudioEditor src={audioSrc} className="w-full" readonly />;
          },
        }}
      />
    </div>
  );
};

// 07/26/2024: Special case for Bee Math. See https://scaleapi.slack.com/archives/C05P745UG1Y/p1721839018226419.
// Replaces \\[ ... \\] and \\( ... \\) with $$ ... $$.
export const replaceWithDoubleDollarSign = (markdown: string): string => {
  let result = markdown.replace(
    /\\\\\[((?:(?!\\\\\[).)*?)\\\\\]/gs,
    (_match, latex) => `$$${latex}$$`,
  );
  result = result.replace(/\\\\\(((?:(?!\\\\\().)*?)\\\\\)/gs, (_match, latex) => `$$${latex}$$`);
  return result;
};

export const ChatMarkdown = React.memo(ChatMarkdownUnmemoized);
